import { useMediaQuery } from "@material-ui/core";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Label,
  PieProps,
} from "recharts";
import { graphColors } from "utility/graphColors";
import { asPercent } from "utility/numbers";

interface Props {
  data: { name: string; value?: number }[];
  label: string;
  loading?: boolean;
}

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = (
  props: PieProps,
  otherLabels: { value?: number; name: string }[]
) => {
  const {
    cx = 0,
    cy = 0,
    startAngle = 0,
    endAngle = 0,
    innerRadius = 0,
    outerRadius = 0,
    value,
    name,
  } = props as PieProps & { value: number; name: string };

  if (otherLabels.some((ol) => ol.name === name)) {
    // tslint:disable-next-line: no-null-keyword
    return null;
  }

  const formattedStartAngle = startAngle + 270;

  const isStraightLine =
    formattedStartAngle > 360 * 0.95 ||
    (formattedStartAngle < 360 * 0.55 && formattedStartAngle > 360 * 0.45);

  const radius =
    (innerRadius as number) +
    (outerRadius as number) -
    (innerRadius as number) * 0.5;
  let x: number | string =
    (cx as number) + radius * Math.cos(-startAngle * RADIAN);
  const midAngle = (startAngle - Math.abs(endAngle)) / 2;

  const position =
    startAngle - Math.abs(startAngle - Math.abs(midAngle)) > 45
      ? Math.ceil((startAngle + 270) / 90)
      : Math.ceil((midAngle + 270) / 90);
  x = position === 4 || position === 3 ? "100%" : "0%";

  const yPosition =
    (cy as number) +
    ((innerRadius as number) + 30) * Math.sin(-startAngle * RADIAN) +
    (position === 3 || position === 4 ? 31 : -27);
  const y =
    yPosition > 150
      ? Math.min(isStraightLine ? 300 : yPosition, 300 - 22 - 31)
      : Math.max(isStraightLine ? 0 : yPosition, 22);

  return (
    <text
      x={x}
      y={y}
      fill="black"
      textAnchor={x === "0%" ? "start" : "end"}
      dominantBaseline="central"
      fontSize="0.875em"
    >
      <tspan dy="0em" x={x} fill={props.fill ?? "#A4A4A4"}>
        {name}
      </tspan>
      <tspan dy="1em" x={x}>
        {asPercent(value)}
      </tspan>
    </text>
  );
};

const renderCustomizedLabelLine = (
  props: PieProps,
  otherLabels: { value?: number; name: string }[],
  containerRef: React.RefObject<ResponsiveContainer> | null
) => {
  const {
    cx = 0,
    cy = 0,
    innerRadius = 0,
    startAngle = 0,
    name,
  } = props as PieProps & {
    points: { x: number; y: number }[];
    name: string;
  };

  if (otherLabels.some((ol) => ol.name === name)) {
    // tslint:disable-next-line: no-null-keyword
    return null;
  }

  const x =
    (cx as number) + (innerRadius as number) * Math.cos(-startAngle * RADIAN);
  const y =
    (cy as number) + (innerRadius as number) * Math.sin(-startAngle * RADIAN);

  const x2 =
    (cx as number) +
    ((innerRadius as number) + 30) * Math.cos(-startAngle * RADIAN);
  const y2 =
    (cy as number) +
    ((innerRadius as number) + 30) * Math.sin(-startAngle * RADIAN);

  const formattedStartAngle = startAngle + 270;

  if (
    formattedStartAngle > 360 * 0.95 ||
    (formattedStartAngle < 360 * 0.55 && formattedStartAngle > 360 * 0.45)
  ) {
    return <path d={`M${x},${y}L${x2},${y2}`} stroke="#CCCCCC" fill="none" />;
  }

  const centerPoint = (containerRef?.current?.state.containerWidth ?? 0) / 2;
  const endPoint = containerRef?.current?.state.containerWidth ?? 0;

  return (
    <path
      d={`M${x},${y}L${x2},${y2}L${x >= centerPoint ? endPoint : 0},${y2}`}
      stroke="#CCCCCC"
      fill="none"
    />
  );
};

const PieChartComponent = (props: Props) => {
  const { data, label, loading } = props;

  const { t } = useTranslation();

  const matchesMediaQuery = useMediaQuery("(min-width: 600px)");

  const containerRef = React.useRef<ResponsiveContainer>(null);

  const filteredData = data
    .filter((d) => d.value)
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

  const otherLabels = filteredData
    .slice(0)
    .reverse()
    .reduce((p, c) => {
      if (p.reduce((pv, cv) => pv + (cv.value ?? 0), 0) + (c.value ?? 0) <= 8) {
        p.push(c);
      }
      return p;
    }, [] as typeof filteredData)
    .reverse();

  const firstHalfLabels = [...otherLabels].slice(
    0,
    Math.ceil(otherLabels.length / 2)
  );
  const secondHalfLabels = [...otherLabels].slice(
    Math.ceil(otherLabels.length / 2)
  );

  return (
    <>
      <ResponsiveContainer width="100%" height={300} ref={containerRef}>
        <PieChart>
          {!loading && filteredData.length > 0 && (
            <Pie
              data={filteredData}
              innerRadius={77}
              outerRadius={87}
              fill="#8884d8"
              paddingAngle={1}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              label={(pieProps: PieProps) =>
                renderCustomizedLabel(pieProps, otherLabels)
              }
              isAnimationActive={false}
              labelLine={(pieProps: PieProps) =>
                renderCustomizedLabelLine(pieProps, otherLabels, containerRef)
              }
            >
              {filteredData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    !otherLabels.some((ol) => ol.name === entry.name)
                      ? (graphColors[data.length - 1 - otherLabels.length] ??
                          graphColors[graphColors.length - 1])[index] ??
                        "#A4A4A4"
                      : "#A4A4A4"
                  }
                />
              ))}
              <Label position="center" value={label} />
            </Pie>
          )}
          {!loading && filteredData.length === 0 && (
            <g>
              <circle
                cx="50%"
                cy="50%"
                r="76"
                stroke="#4A76A1"
                strokeWidth={2}
                fill="none"
              />
              <text
                x="50%"
                y="50%"
                fill="#4A76A2"
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="14"
                fontStyle="italic"
              >
                {t("no_data")}
              </text>
            </g>
          )}
          {loading && (
            <g>
              <linearGradient id="linearLoading" x1="0" y1="0" x2="1" y2="1">
                <stop
                  offset="0%"
                  stopColor="rgba(211, 116, 28, 1)"
                  stopOpacity={0}
                />
                <stop
                  offset="25%"
                  stopColor="rgba(211, 116, 28, 0.5)"
                  stopOpacity={0}
                />
                <stop offset="100%" stopColor="rgba(211, 116, 28, 1)" />
              </linearGradient>
              <circle
                cx="50%"
                cy="50%"
                r="76"
                stroke="url(#linearLoading)"
                strokeWidth={4}
                fill="none"
                className="spin"
              />
            </g>
          )}
          <circle
            cx="50%"
            cy="50%"
            r="74"
            stroke="#CCCCCC"
            strokeWidth={1}
            fill="none"
          />
        </PieChart>
      </ResponsiveContainer>
      {otherLabels.length > 0 && (
        <>
          <p style={{ fontWeight: 700, color: "#A4A4A4", marginBottom: 0 }}>
            {t("rest")}
          </p>
          {matchesMediaQuery ? (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                fontSize: "0.875em",
              }}
            >
              <ul style={{ listStyle: "none", padding: 0, width: "50%" }}>
                {firstHalfLabels.map((v) => (
                  <li key={v.name}>
                    {v.name} {asPercent(v.value)}
                  </li>
                ))}
              </ul>
              <ul
                style={{
                  listStyle: "none",
                  borderLeft:
                    secondHalfLabels.length > 0
                      ? "1px solid #D7D7D7"
                      : undefined,
                  paddingRight: 0,
                  paddingTop: 0,
                  paddingBottom: 0,
                  width: "50%",
                  paddingLeft: 14,
                }}
              >
                {secondHalfLabels.map((v) => (
                  <li key={v.name}>
                    {v.name} {asPercent(v.value)}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div style={{ fontSize: "0.875em" }}>
              <ul style={{ listStyle: "none", padding: 0, width: "100%" }}>
                {otherLabels.map((v) => (
                  <li key={v.name}>
                    {v.name} {asPercent(v.value)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default React.memo(PieChartComponent);
